import { useEffect, useRef } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import { getQueryParamsStringToObject } from 'utils/helpers/router'

export const useOnCreate = <T extends Array<string | number | boolean>>(
  callback: (dependencies: T) => void,
  dependencies: T,
) => {
  const calledOnce = useRef(false)
  useEffect(() => {
    if (calledOnce.current) return
    callback(dependencies)
    calledOnce.current = true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies])
}

export const useOnDestroy = <T extends Array<string | number | boolean>>(
  callback: (dependencies: T) => void,
  dependencies: T,
) => {
  useEffect(
    () => () => {
      callback(dependencies)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [...dependencies],
  )
}

export const useOnCreateWithParamsAndQuery = <
  Query extends Record<string, unknown> | null,
  Params extends Record<string, unknown> | null,
  T extends Array<string | number | boolean>,
>(
  callback: (args: { query: Query; params: Params; pathname: string; dependencies: T }) => void,
  dependencies: T,
) => {
  const calledOnce = useRef(false)
  const params = useParams() as Params
  const { pathname, search } = useLocation()

  useEffect(() => {
    if (calledOnce.current) return
    const query = getQueryParamsStringToObject<Query>(search)
    callback({ query, params, dependencies, pathname })
    calledOnce.current = true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, Object.values(params as Record<string, string>).toString(), ...dependencies])
}

export const useOnListenParamsAndQuery = <
  Query extends Record<string, unknown> | null,
  Params extends Record<string, unknown> | null,
  T extends Array<string | number | boolean>,
>(
  callback: (args: { query: Query; params: Params; dependencies: T }) => void,
  dependencies: T,
) => {
  const params = useParams() as Params
  const { search } = useLocation()
  useEffect(() => {
    const query = getQueryParamsStringToObject<Query>(search)
    callback({ query, params, dependencies })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, Object.values(params as Record<string, string>).toString(), ...dependencies])
}
